import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { vendorAPI } from '../../services/api/vendor'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'

const logger = createLogger('VendorStewardshipRelease')

/** Discreet release-stewardship link with inline 2-step confirm. */
const VendorStewardshipRelease = memo(function VendorStewardshipRelease({ entity, onReleased }) {
  const { t } = useTranslation()
  const [showConfirm, setShowConfirm] = useState(false)
  const { execute, loading } = useAsyncOperation()

  const handleRelease = useCallback((e) => {
    e.stopPropagation()
    execute(() => vendorAPI.releaseStewardship(entity.id), {
      onSuccess: () => {
        toast.success(`Released stewardship of ${entity.name}`)
        setShowConfirm(false)
        onReleased?.(entity.id)
      },
      onError: (err) => {
        logger.error('Release stewardship failed', err)
        toast.error(err?.message || 'Failed to release stewardship')
      },
    })
  }, [entity, execute, onReleased])

  return (
    <div className="mt-3 pt-2.5 border-t border-gray-100">
      {!showConfirm ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowConfirm(true) }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowConfirm(true) }}
          className="text-[11px] text-gray-400 hover:text-red-500 transition-colors"
        >
          {t('vendor.stewardship.release', 'Release stewardship')}
        </button>
      ) : (
        <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs font-semibold text-red-700 leading-snug">
            {t('vendor.stewardship.releaseConfirmTitle', 'Release stewardship of {{name}}?', { name: entity.name })}
          </p>
          <p className="text-[11px] text-red-500 mt-0.5 leading-snug">
            {t('vendor.stewardship.releaseConfirmBody', 'You will lose access to the vendor dashboard for this listing. The entity will remain visible to the community.')}
          </p>
          <div className="flex gap-2 mt-2.5">
            <button
              type="button"
              disabled={loading}
              onClick={handleRelease}
              onTouchEnd={(e) => { e.preventDefault(); handleRelease(e) }}
              className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60"
            >
              {loading ? t('common.saving', 'Saving…') : t('vendor.stewardship.releaseConfirm', 'Yes, release')}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowConfirm(false) }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowConfirm(false) }}
              className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default VendorStewardshipRelease
